import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Divider, IconButton, Menu, Stack, Typography } from '@mui/material';
import { ConfigContext } from '../ConfigContext';
import { useApplicationsList } from '../ApplicationContext';
import { ROUTES } from '../../constants';
import Cookies from 'js-cookie';
import DeloitteBlackLOGO from '../../../public/DeloitteBlackLOGO.svg';
import { Apps, HomeOutlined, DiamondOutlined, CategoryOutlined, DeviceHubOutlined, DashboardOutlined } from '@mui/icons-material';
import './LogoAndProductName.css';
const applicationIcons = [
    HomeOutlined,
    DiamondOutlined,
    CategoryOutlined,
    DeviceHubOutlined,
    DashboardOutlined
];
export function LogoAndProductName() {
    const navigate = useNavigate();
    const { pathname: currentPathname, search } = useLocation();
    const { configState } = React.useContext(ConfigContext);
    const getApplications = useApplicationsList();
    const [menuItems, setMenuItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // State for dropdown menu
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState('');
    // Load applications from database - only once on mount
    useEffect(() => {
        let isMounted = true;
        const loadApplications = async () => {
            if (isLoading || menuItems.length > 0)
                return;
            setIsLoading(true);
            try {
                const apps = await getApplications();
                if (!isMounted)
                    return;
                const applications = apps;
                // Filter out RBAC and build menu items
                const items = applications
                    .filter((app) => app?.details?.productName && app.name !== 'RBAC')
                    .map((app, index) => ({
                    isLink: true,
                    text: app.details?.displayName || app.name,
                    url: `/${app.details?.productName}`,
                    id: app.id,
                    icon: applicationIcons[index] || DashboardOutlined,
                    isDefaultActiveLink: index === 0
                }));
                setMenuItems(items);
            }
            catch (error) {
                console.error('Error loading applications for menu:', error);
            }
            finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        loadApplications();
        return () => {
            isMounted = false;
        };
    }, []); // Empty dependency array - load only once
    // Determine if header menu should be shown
    const showHeaderMenu = JSON.parse(process.env.REACT_APP_SHOW_APPLICATION_MENU ?? 'true') &&
        menuItems.length > 1 &&
        currentPathname !== ROUTES.HOME &&
        currentPathname !== ROUTES.SETTINGS &&
        currentPathname !== ROUTES.USER_MANAGEMENT &&
        currentPathname !== ROUTES.APPS;
    // Update selectedItem when pathname or menuItems change.
    // When the URL carries ?id=, prefer the exact id match so that two deployments
    // sharing the same productName show the correct application name.
    useEffect(() => {
        const idParam = new URLSearchParams(search).get('id');
        const byId = idParam
            ? menuItems.find((item) => String(item.id) === idParam)
            : null;
        const byPath = menuItems.find((item) => currentPathname.includes(item.url));
        setSelectedItem((byId ?? byPath)?.text || '');
    }, [currentPathname, search, menuItems]);
    // Menu event handlers
    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuOpen(false);
    };
    const handleClickItem = (url, text, id) => {
        setSelectedItem(text);
        navigate(`${url}?id=${id}`);
        handleMenuClose();
    };
    const getApplicationNameFromSession = () => {
        const productSelected = sessionStorage.getItem('current_application');
        const applicationName = JSON.parse(productSelected || '{}')?.name;
        return applicationName;
    };
    // Helper function to determine what content should be shown
    const shouldShowDeloitteLogo = configState?.settings?.show_deloitte_logo;
    const shouldShowCustomLogo = configState?.settings?.show_logo &&
        configState?.settings?.header_logo_image;
    const shouldShowWorkspaceName = configState?.settings?.show_workspace_name;
    const shouldShowApplication = currentPathname !== ROUTES.HOME &&
        currentPathname !== ROUTES.SETTINGS &&
        currentPathname !== ROUTES.APPS &&
        currentPathname !== ROUTES.TERMS_OF_USE &&
        selectedItem;
    const shouldShowHelpCenter = currentPathname === ROUTES.HELP && !selectedItem;
    // Function to navigate to product selection
    const handleLogoClick = () => {
        if (JSON.parse(process.env.REACT_APP_SHOW_PRODUCT_SELECTION_PAGE ?? 'false')) {
            // Redirect to product selection page
            navigate(ROUTES.APPS);
        }
        else {
            const firstProduct = Cookies.get('Product Selected')?.trim();
            const isValidProductRouteSegment = Boolean(firstProduct) && firstProduct !== '0';
            if (isValidProductRouteSegment) {
                navigate(`/${firstProduct}`);
                return;
            }
            navigate(ROUTES.HOME);
        }
    };
    // Build array of elements to render
    const elements = [];
    // Add Deloitte logo
    if (shouldShowDeloitteLogo) {
        elements.push(_jsx(Box, { component: "img", src: DeloitteBlackLOGO, alt: "Deloitte", sx: {
                height: 40,
                width: 'auto',
                display: 'flex',
                alignItems: 'center',
                objectFit: 'contain',
                '&:hover': { cursor: 'pointer' }
            }, onClick: handleLogoClick, title: "Go to homepage" }, "deloitte-logo"));
    }
    // Add custom logo (with industry pill grouped together)
    if (shouldShowCustomLogo) {
        elements.push(_jsxs(Box, { sx: {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                height: 32
            }, children: [_jsx(Box, { component: "img", src: configState?.settings?.header_logo_image ?? '', alt: "Custom logo", onClick: handleLogoClick, title: "Go to homepage", sx: {
                        height: 32,
                        cursor: 'pointer',
                        objectFit: 'contain'
                    } }), configState?.settings?.include_badge && configState?.settings?.industry_name && (_jsx(Box, { sx: {
                        backgroundColor: '#000000',
                        color: '#FFFFFF',
                        borderRadius: '16px',
                        padding: '2px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        height: 24,
                        whiteSpace: 'nowrap'
                    }, children: _jsx(Typography, { sx: {
                            fontFamily: 'Open Sans',
                            fontSize: '13px',
                            fontWeight: 600,
                            lineHeight: 1.5,
                            color: 'inherit'
                        }, children: `for ${configState.settings.industry_name}` }) }))] }, "custom-logo-group"));
    }
    // Add workspace name
    if (shouldShowWorkspaceName) {
        elements.push(_jsx(Typography, { variant: "body1", onClick: handleLogoClick, sx: {
                color: `${configState?.settings?.workspace_name_color}`,
                fontFamily: 'Open Sans',
                fontSize: 27,
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: 1,
                letterSpacing: '-0.54px',
                display: 'flex',
                alignItems: 'center',
                height: 32,
                cursor: 'pointer'
            }, dangerouslySetInnerHTML: {
                __html: configState?.settings?.workspace_name ?? ''
            } }, "workspace-name"));
    }
    // Add application name
    if (shouldShowApplication) {
        elements.push(_jsx(Typography, { variant: "body1", sx: {
                color: `${configState?.settings?.workspace_name_color}`,
                fontFamily: 'Open Sans',
                fontSize: 27,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 1,
                letterSpacing: '-0.54px',
                display: 'flex',
                alignItems: 'center',
                height: 40
            }, children: selectedItem }, "application-name"));
    }
    if (shouldShowHelpCenter) {
        elements.push(_jsx(Typography, { variant: "body1", sx: {
                color: `${configState?.settings?.workspace_name_color}`
            }, className: "header-typography-normal", children: getApplicationNameFromSession() }, "help-center-name"));
    }
    // Add dividers between elements (only if there are multiple elements)
    const elementsWithDividers = [];
    for (let index = 0; index < elements.length; index++) {
        const element = elements[index];
        elementsWithDividers.push(element);
        // Add divider after each element except the last one
        if (index < elements.length - 1) {
            // Use the element's key to create a unique divider key
            const elementKey = element.key || `element-${index}`;
            elementsWithDividers.push(_jsx(Divider, { orientation: "vertical", sx: {
                    border: `1px solid ${configState?.settings?.workspace_name_color}`,
                    height: 40,
                    alignSelf: 'center'
                }, flexItem: true }, `divider-after-${elementKey}`));
        }
    }
    return (_jsx(Box, { sx: { flexGrow: 1 }, children: _jsxs(Stack, { direction: "row", spacing: 2, sx: {
                alignItems: 'center',
                height: 40,
                minHeight: 40
            }, children: [showHeaderMenu && (_jsx(IconButton, { edge: "start", "aria-label": "applications menu", "aria-controls": "apps-menu", "aria-haspopup": "true", onClick: handleMenuOpen, color: "inherit", sx: {
                        backgroundColor: menuOpen
                            ? '#EBEBEB'
                            : 'transparent',
                        width: 40,
                        height: 40,
                        padding: 1,
                        '& .MuiSvgIcon-root': {
                            fontSize: '1.5rem'
                        },
                        borderRadius: 1
                    }, children: _jsx(Apps, { sx: { color: '#000' } }) })), elementsWithDividers, showHeaderMenu && (_jsx(Menu, { id: "apps-menu", anchorEl: anchorEl, open: menuOpen, onClose: handleMenuClose, anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                    }, transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                    }, slotProps: {
                        paper: {
                            sx: {
                                marginTop: 1.5,
                                borderRadius: '0 0 8px 8px',
                                borderTop: 'none',
                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                width: `${Math.min(menuItems.length, 4) * 118 + (Math.min(menuItems.length, 4) - 1) * 8 + 48}px`,
                                minWidth: 'auto',
                                maxWidth: 'none',
                                maxHeight: 'calc(100vh - 100px)',
                                overflowY: 'auto'
                            }
                        }
                    }, children: _jsx(Box, { sx: {
                            display: 'grid',
                            gridTemplateColumns: `repeat(${Math.min(menuItems.length, 4)}, 118px)`,
                            gap: '8px',
                            padding: '24px',
                            backgroundColor: '#ffffff'
                        }, children: menuItems.map((item) => (_jsxs(Box, { onClick: () => handleClickItem(item.url, item.text, item.id), sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                width: 118,
                                height: 'auto',
                                minHeight: 106,
                                padding: '12px 13px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                backgroundColor: selectedItem === item.text
                                    ? 'rgba(38, 137, 13, 0.08)'
                                    : 'transparent',
                                '&:hover': {
                                    backgroundColor: 'rgba(38, 137, 13, 0.04)'
                                },
                                transition: 'background-color 0.2s ease'
                            }, children: [_jsx(Box, { sx: {
                                        width: 32,
                                        height: 32,
                                        backgroundColor: '#26890d',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '8px',
                                        flexShrink: 0,
                                        '& svg': {
                                            color: '#ffffff',
                                            fontSize: '20px'
                                        }
                                    }, children: React.createElement(item.icon) }), _jsx(Typography, { variant: "body2", sx: {
                                        fontFamily: 'Open Sans',
                                        fontSize: '14px',
                                        fontWeight: selectedItem === item.text
                                            ? 600
                                            : 400,
                                        lineHeight: 1.62,
                                        color: selectedItem === item.text
                                            ? '#26890D'
                                            : '#212121',
                                        textAlign: 'center',
                                        letterSpacing: '0.17px',
                                        width: '100%',
                                        maxWidth: '92px',
                                        wordBreak: 'break-word',
                                        hyphens: 'auto',
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical'
                                    }, children: item.text })] }, item.text))) }) }))] }) }));
}
